import {
  ENTITY_SPECS,
  NEVER_ARCHIVED_TABLES,
  OMITTED_FIELDS,
  SCOPE_ENTITIES,
  isDeniedSettingKey,
  readZip,
  serializeArchive,
  settingDisposition,
  writeZip,
} from '../archive';
import type { ArchiveBlobReader, ArchiveEntity, ArchiveResult, ArchiveRows } from '../archive';
import { config } from '../config';
import {
  closeRecoveryDb,
  readRecoverySnapshot,
  readRecoveryStore,
  readRecoveryValue,
} from '../data/recoveryDb';
import type { RecoverySnapshot } from '../data/recoveryDb';
import type { LogEntryRow } from '../types';
import { assembleArchiveBlob, BACKUP_MIME, logArchiveReport } from './backupExport';
import { describeUnknownError } from './errors';
import { log, LOG_RING_STORE } from './logger';
import { shareFile } from './shareFile';
import type { ShareRoute } from './shareFile';

/**
 * **§13.5's recovery export** — a real BAK-1 archive out of a database this bundle cannot open.
 *
 * Design §13.5: *"Because `archive/` has no `domain/` dependency, the recovery screen exports a
 * genuine BAK-1 archive from raw rows. A newer client can import it — which is the recovery
 * direction that matters."* This module is that sentence, wired.
 *
 * It is the sibling of `backupExport.ts` and deliberately shaped like it: read rows, hand them to
 * `serializeArchive`, hand the bytes to the operating system. The **only** difference is where the
 * rows come from — `data/recoveryDb.ts`'s schema-less connection instead of `data/repos/profile.ts`
 * — and the one thing that difference forces, which is the subject of most of this note.
 *
 * ── The archive itself is `serializeArchive`'s, byte for byte ────────────────
 *
 * INV-12 names one producer of archive bytes and this is not a second one. Nothing here re-orders,
 * re-compresses or rewrites what the serializer returned: the rescue container is that archive's own
 * entries plus **one** additional file, and `recoveryExport.test.ts` proves it by stripping that file
 * and comparing the rebuilt bytes against `serializeArchive`'s output for equality. Which is also
 * why nothing in `src/archive/` changed for this unit — `readZip` and `writeZip` are used exactly as
 * they already are, from the layer above, where glue belongs.
 *
 * ── `recovery/rescue.json` — why an export must not filter by its own vocabulary ─
 *
 * A newer schema is, by construction, one this bundle has no names for. `data.json` is written from
 * `ENTITY_SPECS`' explicit per-entity field lists (§11.7's determinism control), so a table the
 * newer build added and a field it added to an existing row are **both** invisible to it: they would
 * be read off the device, pass through this process, and not appear in the file the user is told is
 * everything they have. That is not a formatting limitation, it is the export losing the data it
 * exists to save, silently, on the one path where the user has no other copy.
 *
 * So the rescue carries a second entry holding exactly what `data.json` could not:
 *
 * | Bucket | What it holds |
 * |---|---|
 * | `unknownTables` | every row of every store outside this bundle's archive vocabulary, verbatim |
 * | `unknownFields` | per carried row, the properties outside its declared field list, with its key |
 * | `skippedTables` | device-local stores §17.4 excludes, named with their row counts |
 * | `withheldSettingKeys` | §11.8's **declared** refusals — withheld, and **said out loud** |
 * | `unknownSettingKeys` | setting keys a newer build wrote — **named, value not carried** (below) |
 * | `binaryFieldPaths` | properties holding bytes; those travel as `blobs/`, never as JSON |
 * | `unreadableStores` | stores the read path could not open at all — a partial rescue says so |
 *
 * An importer that has never heard of this entry is unaffected: `readArchive` classifies an unknown
 * entry as `unexpectedEntries`, reports it, and imports the archive normally. So the rescue file is
 * a valid BAK-1 archive *and* a complete record, rather than a choice between them.
 *
 * Nothing in `src/archive/` changed for this unit beyond two re-exports of symbols that already
 * existed (`index.ts`) — INV-12's producer, its field lists and its container writer are untouched.
 *
 * ── Settings: the one place a rescue still withholds, and why ────────────────
 *
 * `backupCredential` and `themePreference` are **declared** unarchivable — the first is a secret,
 * the second a property of a device rather than of the data — and neither becomes safe to publish
 * because the app is having a bad day. An **unrecognised** key is withheld too, and the reasoning
 * for that one is set out at {@link buildRecoveryResidue}. Both are recorded **by name**, in
 * separate lists because they are separate facts, so the user is told exactly what did not travel.
 */

const MODULE = 'recovery';

/** The rescue entry's path inside the container. Part of the artifact, so it is declared, not composed. */
export const RESCUE_ENTRY = 'recovery/rescue.json';

/**
 * The rescue payload's own version, independent of `ARCHIVE_FORMAT_VERSION`.
 *
 * They answer different questions and must be able to move separately: the archive's version says
 * what an importer can read, this one says what a human or a future migration tool reading the
 * rescue entry should expect. Bumping one for the other's sake would make `readArchive`'s SYN-9
 * negotiation refuse files it can read perfectly well.
 */
export const RESCUE_PAYLOAD_VERSION = 1;

/**
 * `touchline-rescue-…`, not `touchline-backup-…`.
 *
 * The file *is* an importable backup, so naming it as one would be defensible — but a user who
 * reaches this screen very often already has ordinary backups in the same folder, and the one file
 * that came off a device the app could not open is the one they must not confuse with the others.
 * The name is outside the container (§11.7 forbids a timestamp *inside* it), so this costs the
 * archive's determinism nothing.
 */
const RESCUE_FILENAME_PREFIX = 'touchline-rescue';

export function recoveryFilename(at: number): string {
  const stamp = new Date(at).toISOString().replace(/[:.]/g, '-').replace(/-\d{3}Z$/, 'Z');
  return `${RESCUE_FILENAME_PREFIX}-${stamp}.zip`;
}

// ─────────────────────────────────────────────────────────────────────────────
// The residue — everything `data.json` cannot carry
// ─────────────────────────────────────────────────────────────────────────────

export interface SkippedTable {
  readonly name: string;
  readonly rows: number;
  readonly why: string;
}

export interface RecoveryResidue {
  readonly rescueVersion: number;
  readonly storedVersion: number;
  readonly bundleVersion: number;
  readonly storeNames: readonly string[];
  readonly rowCounts: Readonly<Record<string, number>>;
  readonly unknownTables: Readonly<Record<string, readonly unknown[]>>;
  readonly unknownFields: Readonly<Record<string, readonly Record<string, unknown>[]>>;
  readonly skippedTables: readonly SkippedTable[];
  /** §11.8's **declared** refusals — `backupCredential`, `themePreference`. Named, never carried. */
  readonly withheldSettingKeys: readonly string[];
  /**
   * Setting keys a newer build wrote that this bundle has never heard of.
   *
   * **The key is named; the value is not carried.** See {@link buildRecoveryResidue} for why this is
   * the one place a rescue deliberately keeps less than it could.
   */
  readonly unknownSettingKeys: readonly string[];
  readonly binaryFieldPaths: readonly string[];
  /** Stores the read path could not open at all. Empty in every healthy case — see `recoveryDb`. */
  readonly unreadableStores: readonly string[];
}

/**
 * Entities the `all` scope actually carries — read from `SCOPE_ENTITIES`, never restated.
 *
 * The residue's correctness is *"whatever `data.json` does not carry travels here"*, and that is
 * only a mechanism if the two are derived from one declaration. Restating the list would make it a
 * claim, and the day somebody dropped an entity from the `all` scope its rows would stop appearing
 * in `data.json` and would not start appearing here.
 */
const CARRIED: ReadonlySet<string> = new Set<string>(SCOPE_ENTITIES.all);

/**
 * The one store in `NEVER_ARCHIVED_TABLES` that a `scope: 'all'` archive **does** carry.
 *
 * `fields.ts` puts it there because *"the OBS-1 ring is not a JSON entity"* — it ships as
 * `debug/log.ndjson` and only in a BAK-1/§13.5 export, which is precisely what this is. Listing it
 * among the skipped stores would put a sentence in the rescue file saying the log was left behind
 * while the log sat in the same container two entries away, and a rescue file that misdescribes
 * itself is worse than one that says less.
 *
 * **Since B-22 the entry is filled from the harness database rather than from this one**
 * ({@link readRescueRing}), which makes the skip *more* correct rather than less: the store of this
 * name in the database being recovered has never been written by any code path, so before B-22 the
 * rescue both omitted it from the residue **and** carried an empty log — the worst of the two.
 */
const CARRIED_AS_DEBUG_LOG = LOG_RING_STORE;

/**
 * Properties `fields.ts` declares absent **by construction** rather than through `OMITTED_FIELDS`.
 *
 * Its own words for `audioBlobs`: *"`blob` and `chunks` are absent by construction rather than by
 * omission: the bytes are a **file** in the zip … and `chunks` is a pre-finalize staging area"* —
 * which is why `OMITTED_FIELDS.audioBlobs` is empty and these two are not in it. `readRecoverySnapshot`
 * already removes them whenever they hold bytes; naming them here covers the one case it cannot,
 * an **empty** `chunks: []`, which is JSON-representable and would otherwise be reported as an
 * unaccounted field on every finalized recording in the profile.
 */
const BY_CONSTRUCTION_ABSENT: Partial<Record<ArchiveEntity, readonly string[]>> = {
  audioBlobs: ['blob', 'chunks'],
};

/** Every field name `data.json` accounts for, published or deliberately absent. */
function accountedFields(entity: ArchiveEntity): ReadonlySet<string> {
  return new Set<string>([
    ...ENTITY_SPECS[entity].fields,
    ...OMITTED_FIELDS[entity],
    ...(BY_CONSTRUCTION_ABSENT[entity] ?? []),
  ]);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * The properties of one row that its entity's field list does not account for, keyed by the row.
 *
 * Returns `null` — not an empty object — when there are none, so a healthy row costs nothing and the
 * emitted map holds only rows that actually carry something. `sortBy` is included so the extras can
 * be put back beside the right row by a human or a later tool; a row with no key at all still
 * travels, because a value with an unclear owner is better than a value that is gone.
 */
function unaccountedProperties(
  entity: ArchiveEntity,
  row: unknown,
  accounted: ReadonlySet<string>,
): Record<string, unknown> | null {
  if (!isRecord(row)) return null;
  const extras: Record<string, unknown> = {};
  let found = false;
  for (const key of Object.keys(row).sort()) {
    if (accounted.has(key)) continue;
    extras[key] = row[key];
    found = true;
  }
  if (!found) return null;
  const sortBy = ENTITY_SPECS[entity].sortBy;
  return { [sortBy]: row[sortBy], ...extras };
}

/**
 * Builds the rescue payload from the raw snapshot. Pure — no I/O, so it is directly testable.
 *
 * ── The one place this rescue deliberately keeps less than it could ─────────
 *
 * Everywhere else in this module the rule is *carry it, this bundle's vocabulary is not the limit*.
 * The **settings key space** is the exception, and `fields.ts` states the reason in the direction
 * that matters here: *"**Writing** an archive, unknown must mean deny: a key whose sensitivity this
 * bundle cannot know must not be published by this bundle."* Its declared-vs-unknown asymmetry — the
 * one that says an unrecognised key is a forward-compatibility finding rather than a security one —
 * is written for **reading** an archive, and applying it to a write would publish, into a file the
 * user is about to share, a value a later build stored under a name this build cannot assess. The
 * settings table is the declared home of secrets in this schema (`backupCredential` lives there);
 * no other table carries such a declaration, which is why unknown *tables* travel whole and this
 * does not.
 *
 * So the key is **named** and the value is left behind. That still discharges "never silently
 * dropped" — the user is told exactly which preference did not travel, and it is readable in the
 * newer app that wrote it — while keeping §11.8 true of every archive this bundle produces. An
 * unknown *field* on a **recognised** setting is a different thing (a property of a published
 * setting, not a setting this bundle cannot assess) and travels with the other unknown fields.
 */
export function buildRecoveryResidue(snapshot: RecoverySnapshot): RecoveryResidue {
  const unknownTables: Record<string, readonly unknown[]> = {};
  const unknownFields: Record<string, Record<string, unknown>[]> = {};
  const skippedTables: SkippedTable[] = [];
  const withheldSettingKeys: string[] = [];
  const unknownSettingKeys: string[] = [];

  for (const store of snapshot.storeNames) {
    const rows = snapshot.stores[store] ?? [];

    // Carried, just not as a `data.json` entity — see the constant's note.
    if (store === CARRIED_AS_DEBUG_LOG) continue;

    if (!CARRIED.has(store)) {
      const why = NEVER_ARCHIVED_TABLES[store];
      // A store §17.4 names is excluded for a reason that still holds on this path — but the reason
      // is printed with the row count, so "nothing was lost" is checkable rather than asserted.
      if (why !== undefined) skippedTables.push({ name: store, rows: rows.length, why });
      else unknownTables[store] = rows;
      continue;
    }

    const entity = store as ArchiveEntity;
    const accounted = accountedFields(entity);
    const extras: Record<string, unknown>[] = [];

    for (const row of rows) {
      // The only rows the `all` scope refuses are settings whose key it will not publish. Both
      // reasons are recorded by NAME, and neither carries the value — see the module note above.
      if (entity === 'settings' && isRecord(row)) {
        const key = typeof row.key === 'string' ? row.key : '';
        if (settingDisposition(key) !== 'archive') {
          (isDeniedSettingKey(key) ? withheldSettingKeys : unknownSettingKeys).push(key);
          continue;
        }
      }
      const found = unaccountedProperties(entity, row, accounted);
      if (found !== null) extras.push(found);
    }

    if (extras.length > 0) unknownFields[entity] = extras;
  }

  return {
    rescueVersion: RESCUE_PAYLOAD_VERSION,
    storedVersion: snapshot.storedVersion,
    bundleVersion: snapshot.bundleVersion,
    storeNames: snapshot.storeNames,
    rowCounts: snapshot.rowCounts,
    unknownTables,
    unknownFields,
    skippedTables,
    withheldSettingKeys: withheldSettingKeys.sort(),
    unknownSettingKeys: unknownSettingKeys.sort(),
    binaryFieldPaths: snapshot.binaryFieldPaths,
    unreadableStores: snapshot.unreadableStores,
  };
}

/**
 * JSON that cannot be defeated by the data it is rescuing.
 *
 * `JSON.stringify` throws on a `BigInt` and on a cycle, and IndexedDB's structured clone stores
 * both. On any other path that would be a bug to fix at the source; here the source is a *future*
 * schema this bundle has never seen, so a throw would mean the rescue file the user is standing
 * there waiting for never gets written at all. Neither substitution is silent — each leaves a marker
 * a reader can search for.
 */
export function stringifyRescue(residue: RecoveryResidue): string {
  const seen = new WeakSet<object>();
  return JSON.stringify(
    residue,
    (_key, value: unknown) => {
      if (typeof value === 'bigint') return `[bigint ${value.toString()}]`;
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) return '[circular reference — not representable in JSON]';
        seen.add(value);
      }
      return value;
    },
    2,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// The export
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The snapshot's rows, in the shape `archive/` accepts. A missing store is empty, never absent.
 *
 * The casts are the honest expression of this path, not a shortcut around a type: these rows came
 * off a connection that **declared no schema**, so `unknown` is what is actually known about them.
 * `archive/` is built for exactly that — `serializeArchive` walks rows through an explicit field
 * list and reports what it could not represent rather than trusting a declared shape (its module
 * note: *"Nothing here throws on bad data"*). Writing the members out one by one rather than
 * assembling the object generically keeps the compiler checking this against `ArchiveRows`, so an
 * entity added to the format is a build error here instead of an empty array in a rescue file.
 */
function toArchiveRows(snapshot: RecoverySnapshot): ArchiveRows {
  const of = (entity: ArchiveEntity): readonly unknown[] => snapshot.stores[entity] ?? [];
  return {
    teams: of('teams') as ArchiveRows['teams'],
    players: of('players') as ArchiveRows['players'],
    matches: of('matches') as ArchiveRows['matches'],
    hypotheses: of('hypotheses') as ArchiveRows['hypotheses'],
    observations: of('observations') as ArchiveRows['observations'],
    flagResolutions: of('flagResolutions') as ArchiveRows['flagResolutions'],
    setPieces: of('setPieces') as ArchiveRows['setPieces'],
    talkPoints: of('talkPoints') as ArchiveRows['talkPoints'],
    matchNotes: of('matchNotes') as ArchiveRows['matchNotes'],
    annotations: of('annotations') as ArchiveRows['annotations'],
    coachCards: of('coachCards') as ArchiveRows['coachCards'],
    ourPlayers: of('ourPlayers') as ArchiveRows['ourPlayers'],
    audioBlobs: of('audioBlobs') as ArchiveRows['audioBlobs'],
    templates: of('templates') as ArchiveRows['templates'],
    settings: of('settings') as ArchiveRows['settings'],
  };
}

/**
 * Reads one recording's bytes off the recovery connection.
 *
 * The assembly rule is `backupExport.assembleArchiveBlob`, shared rather than repeated — an
 * unfinalized recording (`blob: null`, bytes still in `chunks`) is exported here on exactly the same
 * terms as it would be by BAK-1, because AUD-3's *"captured audio is never discarded"* does not have
 * a weaker version for a bad day.
 */
function recoveryBlobReader(name: string | undefined): ArchiveBlobReader {
  return async (id: string) =>
    assembleArchiveBlob(
      (await readRecoveryValue('audioBlobs', id, name)) as
        | { blob?: Blob | null; chunks?: Blob[]; mime?: string }
        | undefined,
    );
}

/**
 * The OBS-1 ring for the rescue — read from the **harness** database, and never a reason to fail.
 *
 * Build unit B-22. The finding is in `app/logger.ts`'s `LOG_RING_STORE` note: the logger writes the
 * ring to the harness database and both export paths were reading a same-named store on the product
 * database that nothing writes, so no export carried the log. `readWholeProfile` is the BAK-1 half;
 * this is §13.5's, and it is the half that mattered most — the day the app cannot open its own
 * database is the day a coach most needs the log to leave the phone.
 *
 * ── Two things it does differently from BAK-1's read ─────────────────────────
 *
 * 1. **Schema-less.** `readRecoveryStore`'s note has the reasoning: this screen never writes, and a
 *    declared schema can create an absent database and can truncate a newer one's fields.
 * 2. **It degrades instead of throwing.** §13.5: *"an export must survive a damaged profile rather
 *    than refuse to run on one."* An unreadable — or simply absent, on a device that has never
 *    flushed — ring must not cost the user the rescue file. The failure is reported (NFR-8) and the
 *    container ships without `debug/log.ndjson`, which is exactly what BAK-1 produces for an empty
 *    ring, so the two paths still say the same thing with the same bytes. It is deliberately **not**
 *    recorded in {@link RecoveryResidue}: that structure describes the stores of the database being
 *    recovered, and a second database's read failure inside it would be the misdescription the
 *    {@link CARRIED_AS_DEBUG_LOG} note refuses.
 */
async function readRescueRing(ringName: string): Promise<readonly LogEntryRow[]> {
  try {
    return (await readRecoveryStore(LOG_RING_STORE, ringName)) as readonly LogEntryRow[];
  } catch (cause) {
    log.error(MODULE, '§13.5: the OBS-1 ring could not be read; the rescue carries no debug log', {
      ring: ringName,
      store: LOG_RING_STORE,
      error: describeUnknownError(cause),
    });
    return [];
  }
}

export interface RecoveryArchive {
  /** The genuine BAK-1 archive, exactly as `serializeArchive` produced it. */
  readonly result: ArchiveResult;
  /** That archive plus `recovery/rescue.json`. This is what the user receives. */
  readonly bytes: Uint8Array;
  readonly residue: RecoveryResidue;
}

/**
 * Builds the rescue archive. **Reads only** — nothing on this path writes, migrates or deletes.
 *
 * @param name the database to recover. Defaults to the product database; a parameter for the same
 *   reason every function in `data/recoveryDb.ts` takes one — a test must be able to build a real
 *   newer-schema database beside the one it then imports the result into.
 * @param ringName the database holding the OBS-1 ring. Defaults to the harness database, which is
 *   where `app/logger.ts` writes it; a parameter for the same testability reason as `name`.
 * @throws {StorageError} when the database cannot be opened or read. **Never** for the ring —
 *   see {@link readRescueRing}.
 * @throws {ArchiveError} only for a container the zip format cannot represent. Row-level damage is
 *   reported, never thrown — §13.5's whole argument is that an export must survive a damaged profile
 *   rather than refuse to run on one, and a profile this bundle cannot parse is the extreme case.
 */
export async function buildRecoveryArchive(
  name?: string,
  ringName: string = config.HARNESS_DB_NAME,
): Promise<RecoveryArchive> {
  // The ring first, and that order is deliberate: `openRecoveryDb` holds ONE connection keyed by
  // name, so reading a second database between the snapshot and the blob reads would close and
  // reopen the product connection mid-serialize. Read the ring, then the profile, and the connection
  // the blob reader wants is the one already open.
  const logEntries = await readRescueRing(ringName);
  const snapshot = await readRecoverySnapshot(name);
  const residue = buildRecoveryResidue(snapshot);

  const result = await serializeArchive({
    rows: toArchiveRows(snapshot),
    scope: { kind: 'all' },
    readBlob: recoveryBlobReader(name),
    logEntries,
  });

  // The archive's own entries, unmodified, plus one. `readZip` verifies every CRC on the way in, so
  // the rebuilt container is assembled from entries that were checked rather than assumed.
  const entries = readZip(result.bytes).map((record) => ({ name: record.name, data: record.data }));
  const bytes = writeZip([
    ...entries,
    { name: RESCUE_ENTRY, data: new TextEncoder().encode(stringifyRescue(residue)) },
  ]);

  logArchiveReport(result);
  log.warn(MODULE, '§13.5: built a rescue archive from a database this bundle cannot open', {
    storedVersion: snapshot.storedVersion,
    bundleVersion: snapshot.bundleVersion,
    storedNativeVersion: snapshot.storedNativeVersion,
    archiveBytes: result.bytes.length,
    rescueBytes: bytes.length,
    unknownTables: Object.keys(residue.unknownTables),
    unknownFieldEntities: Object.keys(residue.unknownFields),
    withheldSettingKeys: residue.withheldSettingKeys,
    unknownSettingKeys: residue.unknownSettingKeys,
    unreadableStores: residue.unreadableStores,
    skippedTables: residue.skippedTables.map((table) => `${table.name}:${String(table.rows)}`),
  });

  return { result, bytes, residue };
}

export interface RecoveryExport {
  readonly archive: RecoveryArchive;
  readonly filename: string;
  readonly route: ShareRoute;
}

/**
 * §13.5 end to end: build, then hand the file to the operating system.
 *
 * `shareFile` is BAK-1's own route ladder, used unchanged — the share sheet, then a download, both
 * failing loudly. A rescue that silently did nothing is the worst outcome this screen has, because
 * the user would put the phone away believing they had the file.
 *
 * @param at the instant the filename is stamped with. Passed in, never sampled here.
 * @throws {ExportError} when the user cancels the share sheet, or every route failed. The archive
 *   was built either way, so a retry need not rebuild it.
 * @throws {StorageError} on a read failure while building.
 */
export async function exportRecoveryArchive(at: number): Promise<RecoveryExport> {
  const archive = await buildRecoveryArchive();
  const filename = recoveryFilename(at);
  const route = await shareFile({
    bytes: archive.bytes,
    filename,
    mime: BACKUP_MIME,
    title: 'Touchline rescue archive',
    subject: 'rescue archive',
  });
  return { archive, filename, route };
}

/** Releases the recovery connection. The screen calls this when it unmounts. */
export function closeRecovery(): void {
  closeRecoveryDb();
}

/**
 * The situation, for the screen to describe — re-exported rather than reached for.
 *
 * `ui-b/` talks to `app/` and `app/` talks to `data/`; the only modules in this tree that import
 * `data/` from a component are the two measurement harness screens, which are scheduled for
 * deletion. A recovery screen is not the place to start a second precedent, and a facade whose only
 * job is to keep a layer boundary honest is a facade that has earned its line.
 */
export { readRecoveryOverview } from '../data/recoveryDb';
export type { RecoveryOverview } from '../data/recoveryDb';
